"use client";
import { useEffect, useRef } from "react";
import Link from "next/link";

/**
 * MagneticCta — CTA mit sanftem Magnet-Hover-Effekt.
 *
 * A11y: Wrapper-Span hat KEIN tabindex/role; das interne <a> bleibt der
 * einzige fokussierbare Knoten. Magnet-Translate wird mit transform auf
 * einem Inner-Wrapper geloest — der Fokus-Outline läuft sauber um den
 * gesamten CTA. Touch-Geraete (hover:none) und prefers-reduced-motion
 * bekommen keinen Magnet-Effekt.
 */
type AnchorProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  href: string;
  children: React.ReactNode;
  className?: string;
};

export function MagneticCta({ href, children, className = "", style, ...rest }: AnchorProps) {
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  const innerRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const wrap = wrapRef.current;
    const inner = innerRef.current;
    if (!wrap || !inner) return;

    const prefersReduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const isTouch = window.matchMedia("(hover: none)").matches;
    if (prefersReduce || isTouch) return;

    let raf = 0;
    let target = { x: 0, y: 0 };
    let current = { x: 0, y: 0 };

    const onMove = (e: PointerEvent) => {
      const rect = wrap.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      // Magnet-Stärke ~22% der Distanz, gecappt auf 12px
      target.x = Math.max(-12, Math.min(12, (e.clientX - cx) * 0.22));
      target.y = Math.max(-10, Math.min(10, (e.clientY - cy) * 0.22));
      schedule();
    };
    const onLeave = () => {
      target.x = 0;
      target.y = 0;
      schedule();
    };
    const schedule = () => {
      if (raf) return;
      raf = requestAnimationFrame(animate);
    };
    const animate = () => {
      current.x += (target.x - current.x) * 0.18;
      current.y += (target.y - current.y) * 0.18;
      inner.style.transform = `translate(${current.x.toFixed(2)}px, ${current.y.toFixed(2)}px)`;
      if (Math.abs(target.x - current.x) > 0.1 || Math.abs(target.y - current.y) > 0.1) {
        raf = requestAnimationFrame(animate);
      } else {
        raf = 0;
      }
    };

    wrap.addEventListener("pointermove", onMove);
    wrap.addEventListener("pointerleave", onLeave);
    return () => {
      wrap.removeEventListener("pointermove", onMove);
      wrap.removeEventListener("pointerleave", onLeave);
      if (raf) cancelAnimationFrame(raf);
      inner.style.transform = "";
    };
  }, []);

  // Externe URLs (http/https/mailto) -> normaler <a>, Next-Routes -> Link
  const isExternal = /^(https?:|mailto:|tel:)/i.test(href);
  const Anchor = isExternal ? (
    <a href={href} className={className} style={style} {...rest}>
      <span ref={innerRef} style={{ display: "inline-flex", alignItems: "center", gap: "inherit" }}>
        {children}
      </span>
    </a>
  ) : (
    <Link href={href} className={className} style={style} {...(rest as any)}>
      <span ref={innerRef} style={{ display: "inline-flex", alignItems: "center", gap: "inherit" }}>
        {children}
      </span>
    </Link>
  );

  return (
    <span
      ref={wrapRef}
      style={{ display: "inline-block", lineHeight: 0 }}
    >
      {Anchor}
    </span>
  );
}
